/**
 * SchichtVorschauModal – Zeigt die nächsten 8 Wochen basierend auf dem
 * definierten Schichtrhythmus. Kalender-Blöcke werden mit einem blauen
 * Punkt markiert. Genau dieselbe Optik wie SchichtRhythmusModal.
 */
import React, { useMemo } from 'react';
import { X } from 'lucide-react';

const WD_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const SCHICHT_COLOR = {
  Früh:  'bg-orange-100 text-orange-800 border-orange-300',
  Spät:  'bg-blue-100 text-blue-800 border-blue-300',
  Nacht: 'bg-purple-100 text-purple-800 border-purple-300',
  Frei:  'bg-teal-50 text-teal-600 border-teal-200',
};

const SCHICHT_SHORT = { Früh: 'F', Spät: 'S', Nacht: 'N', Frei: '·' };

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}

function isoWeek(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const jan4 = new Date(d.getFullYear(), 0, 4);
  const sw = new Date(jan4);
  sw.setDate(jan4.getDate() - (jan4.getDay() || 7) + 1);
  return Math.ceil(((d - sw) / 86400000 + 1) / 7);
}

export default function SchichtVorschauModal({ rhythmus, schichtZeiten, onClose }) {
  const today = new Date().toISOString().split('T')[0];

  const weeks = useMemo(() => {
    const rhythmusStart = new Date(rhythmus.startDate + 'T12:00:00').getTime();
    // Beginn der aktuellen Woche (Montag)
    const todayWd = (new Date(today + 'T12:00:00').getDay() + 6) % 7; // Mo=0
    const monday = addDays(today, -todayWd);
    const WEEKS = 8;
    const result = [];

    for (let w = 0; w < WEEKS; w++) {
      const weekStart = addDays(monday, w * 7);
      const days = [];

      for (let d = 0; d < 7; d++) {
        const dateStr = addDays(weekStart, d);
        const cursorNoon = new Date(dateStr + 'T12:00:00').getTime();
        const offset = Math.round((cursorNoon - rhythmusStart) / 86400000);

        let schicht = null;
        let pos = -1;
        if (offset >= 0) {
          pos = ((offset % rhythmus.cycleLength) + rhythmus.cycleLength) % rhythmus.cycleLength;
          schicht = rhythmus.days[pos] ?? 'Frei';
        }

        // Prüfe ob an diesem Tag ein Kalender-Block aktiv ist
        let hasBlock = false;
        let blockTime = null;
        if (schicht !== null && pos >= 0) {
          const z = schichtZeiten?.[schicht];
          if (z?.calendarBlock) {
            const isEnabled =
              !z.type || z.type === 'alle' ||
              (z.type === 'bestimmte' && (z.enabledCyclePositions || []).includes(pos));
            if (isEnabled) {
              hasBlock = true;
              blockTime = z.time || '08:00';
            }
          }
        }

        days.push({
          dateStr,
          dayNum: new Date(dateStr + 'T12:00:00').getDate(),
          schicht,
          isToday: dateStr === today,
          isPast: dateStr < today,
          hasBlock,
          blockTime,
        });
      }

      result.push({ kw: isoWeek(weekStart), days });
    }
    return result;
  }, [rhythmus, schichtZeiten, today]);

  // Zähle aktive Kalender-Blöcke in den nächsten 8 Wochen
  const blockCount = useMemo(() =>
    weeks.reduce((acc, week) =>
      acc + week.days.filter(d => !d.isPast && d.hasBlock).length, 0),
  [weeks]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[500] p-3" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[88vh] flex flex-col overflow-hidden"
           onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex-shrink-0 border-b border-slate-100 px-5 py-3 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-slate-800 text-base">Vorschau – nächste 8 Wochen</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Zyklus: {rhythmus.cycleLength} Tage · Start: {rhythmus.startDate}
              {blockCount > 0 && ` · ${blockCount} Kalender-Blöcke`}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Kalender-Grid */}
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {/* Wochentag-Header */}
          <div className="grid grid-cols-8 gap-0.5 mb-1 sticky top-0 bg-white py-1 z-10">
            <div className="text-[9px] text-slate-300 text-center">KW</div>
            {WD_SHORT.map(d => (
              <div key={d} className="text-[10px] font-semibold text-slate-500 text-center">{d}</div>
            ))}
          </div>

          {/* Wochen-Zeilen */}
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-8 gap-0.5 mb-0.5">
              <div className="text-[9px] text-slate-300 flex items-center justify-center">
                {week.kw}
              </div>
              {week.days.map(({ dateStr, dayNum, schicht, isToday, isPast, hasBlock, blockTime }) => (
                <div key={dateStr}
                  className={`
                    relative rounded-lg border text-center py-1 transition-all
                    ${schicht ? SCHICHT_COLOR[schicht] : 'bg-white border-slate-100 text-slate-200'}
                    ${isToday ? 'ring-2 ring-emerald-500 ring-offset-1 shadow-sm' : ''}
                    ${isPast ? 'opacity-40' : ''}
                  `}
                >
                  <div className={`text-[9px] leading-none ${isToday ? 'font-bold text-emerald-600' : 'opacity-60'}`}>
                    {dayNum}{isToday ? '●' : ''}
                  </div>
                  <div className="text-[11px] font-bold leading-none mt-0.5">
                    {schicht ? SCHICHT_SHORT[schicht] : ''}
                  </div>
                  {hasBlock && (
                    <div
                      className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-blue-500 rounded-full border border-white"
                      title={`Kalender-Block: ${blockTime}`}
                    />
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Legende + Schließen */}
        <div className="flex-shrink-0 border-t border-slate-100 px-4 py-3 space-y-2.5">
          <div className="flex gap-1.5 flex-wrap items-center">
            {['Früh', 'Spät', 'Nacht', 'Frei'].map(s => (
              <span key={s} className={`px-2 py-0.5 rounded-md text-[10px] font-semibold border ${SCHICHT_COLOR[s]}`}>
                {SCHICHT_SHORT[s]} {s}
              </span>
            ))}
            <span className="flex items-center gap-1 ml-1">
              <span className="inline-block w-2 h-2 bg-blue-500 rounded-full border border-slate-200" />
              <span className="text-[10px] text-slate-400">Kalender-Block</span>
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold rounded-xl transition-colors"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}
